function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Page, Tooltip, Typography } from 'seagull';
import pluralize from 'pluralize';
import { useGlobalContext, useStore, useStoreActions } from "../../contexts";
import InvoicesTable, { defaultDateFilter } from "../../components/InvoicesTable/InvoicesTable";
import PageLoadingState from "../../components/PageLoadingState";
import { selectors } from "../../store/state-selectors";
import { ErrorPage, NoInvoices } from "../../components/InvoicesEmptyStates/InvoicesEmptyStates";
import { ImportingInvoicesToQBOModal } from "./ImportingInvoicesToQBOModal";
import { QBOButton } from "./QBOButton";
import styles from "./InvoicesDashboard.module.scss";
import { exportCsv, makeInvoicesCsv, invoicesCsvFileName } from "../../utils/csv.utils";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { numberAndUnit } from "../../infra/utils";
window.pluralize = pluralize;
const pageTitle = 'Invoices';

function getInvoicesStatusCounters(salesInvoicesAggregated) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return Object.keys(salesInvoicesAggregated).reduce((acc, key) => {
    const status = key;

    if (options.withoutCounters) {
      acc[status] = undefined;
    } else {
      const outputProperty = acc[status];
      acc[status] = outputProperty ? outputProperty + (salesInvoicesAggregated[status] || 0) : salesInvoicesAggregated[status];
    }

    return acc;
  }, {});
}

function ActualInvoicesDashboard(props) {
  const {
    isLoading,
    invoices,
    invoicesCounters
  } = props;
  const location = useLocation();
  const isVendor = useStore(selectors.user.isVendor);
  const exportCsvFilename = useStore(state => state.appConfig.exportCsvFilename);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const invoicesTableRef = useRef(null);
  const globalContext = useGlobalContext();
  const [downloadingCsv] = useState(false);
  const [scrollEl, setScrollEl] = useState(null);
  const dateFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const dateFilterKey = 'date';
    return params.get(dateFilterKey) || defaultDateFilter;
  }, [location.search]);
  const actionsRequiredCounter = useMemo(() => {
    const invoicesStatusCounters = getInvoicesStatusCounters(invoicesCounters, {
      withoutCounters: dateFilter !== defaultDateFilter
    });
    return (invoicesStatusCounters.requiresPaymentApproval || 0) + (invoicesStatusCounters.paymentActionRequired || 0) + (invoicesStatusCounters.paymentFailed || 0) + (invoicesStatusCounters.paymentStopped || 0);
  }, [invoicesCounters, dateFilter]);

  async function downloadCsv() {
    if (invoicesTableRef.current) {
      const filename = exportCsvFilename || invoicesCsvFileName;
      const rows = invoicesTableRef.current.getRows();
      exportCsv(makeInvoicesCsv(rows, isVendor), filename);
    }
  }

  return invoices.length > 0 ? /*#__PURE__*/React.createElement(Page, {
    "data-testid": "invoices-dashboard",
    header: pageTitle,
    ref: setScrollEl,
    loading: isLoading,
    subheader: /*#__PURE__*/React.createElement(Typography, null, "Manage and review your invoices", actionsRequiredCounter > 0 ? ". ".concat(numberAndUnit(actionsRequiredCounter, 'invoice'), " require").concat(actionsRequiredCounter > 1 ? '' : 's', " your attention.") : null),
    actions: isVendor && /*#__PURE__*/React.createElement(Tooltip, {
      title: showQboIntegration ? '' : 'Export the invoices displayed on this page to a CSV file in the format required for QuickBooks',
      placement: "bottom-end"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(QBOButton, {
      "data-testid": "download-invoices-csv",
      hideIcon: showQboIntegration,
      loading: downloadingCsv,
      onClick: async () => {
        downloadCsv();

        if (!showQboIntegration) {
          await globalContext.showModal({
            content: _ref => {
              let {
                dismissModal
              } = _ref;
              return /*#__PURE__*/React.createElement(ImportingInvoicesToQBOModal, {
                "data-testid": "importing-invoices-to-qbo-modal",
                onClose: dismissModal,
                downloadCsv: downloadCsv
              });
            }
          });
        }
      }
    }, "Export to CSV")))
  }, /*#__PURE__*/React.createElement(InvoicesTable, _extends({}, props, {
    scrollEl: scrollEl,
    ref: invoicesTableRef
  }))) : /*#__PURE__*/React.createElement(NoInvoices, {
    title: pageTitle,
    isVendor: isVendor
  });
}

export default function InvoicesDashboard() {
  const {
    fetchInvoicesAndCounters
  } = useStoreActions();
  const invoices = useStore(state => state.invoices);
  const invoicesCounters = useStore(state => state.invoicesCounters);
  const {
    USE_INVOICE_VIEWS = false
  } = useFeatureFlags();
  useEffect(() => {
    document.title = pageTitle;
    fetchInvoicesAndCounters(USE_INVOICE_VIEWS);
  }, [USE_INVOICE_VIEWS]);
  const isReady = !!invoices.data && !!invoicesCounters.data;
  const isError = !!invoices.error || !!invoicesCounters.error;
  const isLoading = !!invoices.loading || !!(invoicesCounters !== null && invoicesCounters !== void 0 && invoicesCounters.loading);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isReady && /*#__PURE__*/React.createElement(ActualInvoicesDashboard, {
    invoices: invoices.data,
    invoicesCounters: invoicesCounters.data,
    retry: () => fetchInvoicesAndCounters(USE_INVOICE_VIEWS),
    isLoading: isLoading,
    filtersClassname: styles.filters
  }), !isReady && isLoading && /*#__PURE__*/React.createElement(PageLoadingState, {
    header: pageTitle,
    loading: isLoading,
    "data-testid": "invoices-dashboard-loading"
  }), isError && /*#__PURE__*/React.createElement(ErrorPage, {
    title: pageTitle,
    retry: () => fetchInvoicesAndCounters(USE_INVOICE_VIEWS)
  }));
}